import { zodResolver } from "@hookform/resolvers/zod";
import { useCheckSealdSessions } from "apollo/hooks/mutations";
import { useGetFilesQuery, useGetPatientQuery } from "apollo/hooks/queries";
import { Result, composeProgress } from "apollo/utils";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { capitaliseFirst } from "core/model/utils/strings";
import { Auction, Patient, SealdSessionAccess } from "core/types";
import { format, fromUnixTime } from "date-fns";
import InfoChip from "ds_legacy/components/InfoChip";
import {
  ERROR_COLOR,
  SUCCESS_COLOR,
  TEXT_DISABLED_COLOR,
} from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { padding } from "ds_legacy/materials/metrics";
import {
  Caption,
  FONT_SIZE_16,
  Subheading,
  Title,
} from "ds_legacy/materials/typography";
import { Button, ControlledInput, Form } from "ds/ui";
import { useAcpNavigationHandlers } from "dsl/hooks";
import { CheckIcon, XCircleIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { SealdDebugSearchFormValues, sealdDebugSearchSchema } from "./schema";
import { DetailsTable, DetailsTableRow } from "./shared";

const formatDate = (createdAt: number | null) =>
  createdAt ? format(fromUnixTime(createdAt), "dd/MM/yy - HH:mm:ss x") : "none";

export default function SessionPage({
  page,
}: {
  page: "file" | "patient" | "request";
}) {
  const [search, setSearch] = useSearchParams();
  const [sessionId, setSessionId] = useState<number | null>(null);

  const [patientQueryProgress, patient] = useGetPatientQuery({
    options: { skip: page !== "patient" || !sessionId },
    patientId: sessionId ?? -1,
  });
  const [
    checkSealdSessions,
    checkSealdSessionsQueryProgress,
    _,
    sealdSessionsPayload,
  ] = useCheckSealdSessions();

  const sealdSessions = sealdSessionsPayload
    ? sealdSessionsPayload.sealdSessions
    : undefined;

  const queryParamName =
    page === "patient"
      ? "patient_id"
      : page === "request"
      ? "auction_request_id"
      : "file_id";
  const sessionIdQueryparam = search.get(queryParamName);

  const methods = useForm<SealdDebugSearchFormValues>({
    defaultValues: { entity_id: sessionIdQueryparam ?? "" },
    resolver: zodResolver(sealdDebugSearchSchema),
  });

  useEffect(() => {
    if (sessionIdQueryparam) {
      setSessionId(Number(sessionIdQueryparam));
    } else {
      setSessionId(null);
    }
    methods.reset({ entity_id: sessionIdQueryparam ?? "" });
  }, [sessionIdQueryparam, methods]);

  useEffect(() => {
    if (sessionId) {
      checkSealdSessions({ [queryParamName]: sessionId });
    }
  }, [queryParamName, sessionId]);

  const onSubmit = methods.handleSubmit(({ entity_id }) => {
    setSessionId(Number(entity_id));
    search.set(queryParamName, entity_id);
    setSearch(search);
  });

  return (
    <>
      <FormProvider {...methods}>
        <Form onSubmit={onSubmit}>
          <div className="flex flex-row items-end gap-2">
            <ControlledInput
              label={`${capitaliseFirst(page)} ID`}
              name="entity_id"
            />
            <Button id="search" type="submit" variant="ghost">
              Search
            </Button>
          </div>
        </Form>
      </FormProvider>
      <Result
        data={{}}
        ErrorComponent={
          <Caption>
            Could not get {page} {sessionId}
          </Caption>
        }
        id="seald-session"
        queryProgress={composeProgress([
          patientQueryProgress,
          checkSealdSessionsQueryProgress,
        ])}
      >
        {() => (
          <>
            <Title>
              {capitaliseFirst(page)} {sessionId}
            </Title>
            <DetailsTable>
              <tbody>
                <DetailsTableRow>
                  <td
                    style={{
                      padding: padding(0),
                    }}
                  >
                    <Subheading>encryption activated for {page}</Subheading>
                  </td>
                  <td
                    style={{
                      padding: padding(0, 4),
                    }}
                  >
                    {sealdSessions?.seald_encryption_context_created_at ? (
                      <HorizontalLayout aligned>
                        <CheckIcon
                          size={FONT_SIZE_16}
                          style={{ color: SUCCESS_COLOR }}
                        />
                        <Caption>
                          {formatDate(
                            sealdSessions.seald_encryption_context_created_at,
                          )}
                        </Caption>
                      </HorizontalLayout>
                    ) : (
                      <XCircleIcon
                        size={FONT_SIZE_16}
                        style={{ color: ERROR_COLOR }}
                      />
                    )}
                  </td>
                </DetailsTableRow>
              </tbody>
            </DetailsTable>
            <Subheading>Sender accesses:</Subheading>
            {sealdSessions?.careseeker_accesses?.length ? (
              <AccessesDetailsTable
                accesses={sealdSessions.careseeker_accesses}
                type="sender"
              />
            ) : (
              <Caption>none</Caption>
            )}
            <Subheading>Receiver accesses:</Subheading>
            {sealdSessions?.careprovider_accesses?.length ? (
              <AccessesDetailsTable
                accesses={sealdSessions.careprovider_accesses}
                type="receiver"
              />
            ) : (
              <Caption>none</Caption>
            )}
            <Subheading>Account accesses:</Subheading>
            {sealdSessions?.account_accesses?.length ? (
              <AccessesDetailsTable
                accesses={sealdSessions.account_accesses}
                type="account"
              />
            ) : (
              <Caption>none</Caption>
            )}
            {page === "patient" && patient && (
              <FilesForPatient patient={patient} />
            )}
          </>
        )}
      </Result>
    </>
  );
}

function AccessesDetailsTable({
  accesses,
  type,
}: {
  accesses: Array<SealdSessionAccess>;
  type: "account" | "receiver" | "sender";
}) {
  const navigationHandlers = useAcpNavigationHandlers();
  return (
    <DetailsTable>
      <tbody>
        {accesses.map((access) => (
          <DetailsTableRow
            key={`${type}-${access.id}`}
            onClick={() => {
              if (type === "account") return;
              const page =
                type === "sender"
                  ? `sender?careseeker_id=${access.id}`
                  : `receiver?careprovider_id=${access.id}`;
              navigationHandlers.goToSealdDebug(page);
            }}
          >
            <td
              style={{
                padding: padding(0),
              }}
            >
              <Subheading>
                #{access.id} {access.name}
              </Subheading>
            </td>
            <td
              style={{
                padding: padding(0, 4),
              }}
            >
              {access.seald_db_access_created_at ? (
                <HorizontalLayout aligned>
                  <CheckIcon
                    size={FONT_SIZE_16}
                    style={{ color: SUCCESS_COLOR }}
                  />
                  <Caption>
                    {formatDate(access.seald_db_access_created_at)}
                  </Caption>
                </HorizontalLayout>
              ) : (
                <XCircleIcon
                  size={FONT_SIZE_16}
                  style={{ color: ERROR_COLOR }}
                />
              )}
            </td>
            <td
              style={{
                padding: padding(2, 4),
              }}
            >
              <VerticalLayout>
                <Caption color={TEXT_DISABLED_COLOR}>
                  seald_db_access:{" "}
                  {formatDate(access.seald_db_access_created_at)}
                </Caption>
                <Caption color={TEXT_DISABLED_COLOR}>
                  seald_access_created_at:{" "}
                  {formatDate(access.seald_access_created_at)}
                </Caption>
                <Caption color={TEXT_DISABLED_COLOR}>
                  pending_access: {formatDate(access.pending_access_created_at)}
                </Caption>
              </VerticalLayout>
            </td>
          </DetailsTableRow>
        ))}
      </tbody>
    </DetailsTable>
  );
}

function FilesForPatient({ patient }: { patient: Patient }) {
  return (
    <VerticalLayout>
      {patient.auctions?.map((auction) => (
        <FilesForAuction key={auction.id} auction={auction} />
      ))}
    </VerticalLayout>
  );
}

function FilesForAuction({ auction }: { auction: Auction }) {
  const navigationHandlers = useAcpNavigationHandlers();
  const getOntology = useGetOntology();
  const [filesQueryProgress, files] = useGetFilesQuery({
    params: `auction_id=${auction.id}`,
  });
  return (
    <>
      <Result
        data={files}
        ErrorComponent={
          <Caption>Could not get files for auction {auction.id}</Caption>
        }
        id="seald-session"
        queryProgress={filesQueryProgress}
      >
        {(files) => (
          <>
            <Title>Files for search {auction.id}</Title>
            {files.length ? (
              <DetailsTable>
                <tbody>
                  {files.map((file) => (
                    <DetailsTableRow
                      key={`file-${file.id}`}
                      onClick={() => {
                        navigationHandlers.goToSealdDebug(
                          `file?file_id=${file.id}`,
                        );
                      }}
                    >
                      <td
                        style={{
                          padding: padding(0),
                        }}
                      >
                        <Subheading>#{file.id}</Subheading>
                      </td>
                      <td
                        style={{
                          padding: padding(0, 4),
                        }}
                      >
                        <InfoChip
                          label={getOntology({
                            key: file.category,
                            type: ONTOLOGIES.FILE_CATEGORY,
                            useShortValues: true,
                          })}
                          tooltip={getOntology({
                            key: file.category,
                            type: ONTOLOGIES.FILE_CATEGORY,
                          })}
                        />
                      </td>
                      <td
                        style={{
                          padding: padding(0, 4),
                        }}
                      >
                        <Caption>{formatDate(file.created_at)}</Caption>
                      </td>
                    </DetailsTableRow>
                  ))}
                </tbody>
              </DetailsTable>
            ) : (
              <Caption>none</Caption>
            )}
          </>
        )}
      </Result>
    </>
  );
}
